export interface VideoPoster {
  /** JPEG poster captured from the video. */
  file: File;
  width: number;
  height: number;
  durationSec: number;
}

/**
 * Capture a poster frame from a local video File, client-side. Loads the file
 * into a hidden <video>, seeks ~1s in (clamped to the clip), and draws that
 * frame to a canvas as a JPEG. Resolves null on any failure (unsupported codec,
 * decode error, timeout) so callers can fall back gracefully. The video is a
 * local object URL, so the canvas is never tainted.
 */
export function captureVideoPoster(file: File): Promise<VideoPoster | null> {
  if (typeof document === "undefined" || !file.type.startsWith("video/")) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    let settled = false;

    const finish = (result: VideoPoster | null) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      resolve(result);
    };

    const timer = setTimeout(() => finish(null), 10_000);
    const fail = () => {
      clearTimeout(timer);
      finish(null);
    };

    video.muted = true;
    video.playsInline = true;
    video.preload = "metadata";
    video.onerror = fail;

    const draw = () => {
      if (settled) return;
      try {
        const width = video.videoWidth;
        const height = video.videoHeight;
        if (!width || !height) return fail();
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return fail();
        ctx.drawImage(video, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return fail();
            clearTimeout(timer);
            const base = file.name.replace(/\.[^.]+$/, "");
            finish({
              file: new File([blob], `${base}-poster.jpg`, { type: "image/jpeg" }),
              width,
              height,
              durationSec: Number.isFinite(video.duration) ? video.duration : 0,
            });
          },
          "image/jpeg",
          0.8,
        );
      } catch {
        fail();
      }
    };

    let needSeek = false;
    video.onloadedmetadata = () => {
      // Seek a bit in to avoid black lead-in frames, clamped to the duration.
      const dur = Number.isFinite(video.duration) ? video.duration : 0;
      if (dur > 0) {
        needSeek = true;
        video.currentTime = Math.min(1, dur / 2); // → onseeked → draw
      }
      // dur ≤ 0 (streaming / unknown): no seek; draw the first frame on loadeddata.
    };
    video.onseeked = () => draw();
    // Fallback when we didn't seek (or a browser doesn't emit seeked for a
    // no-op seek) so capture can't stall to the timeout.
    video.onloadeddata = () => {
      if (!needSeek) draw();
    };

    video.src = url;
  });
}
